import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('failed');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('pending');
      toast.info('Payment status check timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/stripe/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(response.data);
        toast.success('Payment successful!');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('failed');
        toast.error('Payment session expired');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 animate-spin text-accent mx-auto mb-4" />
              <CardTitle>Processing Payment...</CardTitle>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-success mx-auto mb-4" />
              <CardTitle className="text-success">Payment Successful!</CardTitle>
            </>
          )}
          {status === 'failed' && (
            <>
              <XCircle className="w-16 h-16 text-error mx-auto mb-4" />
              <CardTitle className="text-error">Payment Failed</CardTitle>
            </>
          )}
          {status === 'pending' && (
            <>
              <Loader2 className="w-16 h-16 text-warning mx-auto mb-4" />
              <CardTitle className="text-warning">Payment Pending</CardTitle>
            </>
          )}
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {status === 'success' && paymentData && (
            <div className="bg-slate-100 rounded-lg p-4">
              <p className="text-sm text-slate-600">Amount Paid</p>
              <p className="text-2xl font-bold text-primary">
                ${paymentData.amount} {paymentData.currency?.toUpperCase()}
              </p>
            </div>
          )}
          
          {status === 'checking' && (
            <p className="text-sm text-slate-500">
              Please wait while we confirm your payment...
            </p>
          )}
          
          {status === 'failed' && (
            <p className="text-sm text-slate-500">
              Your payment could not be processed. Please try again.
            </p>
          )}
          
          {status === 'pending' && (
            <p className="text-sm text-slate-500">
              Payment is still being processed. You'll receive an email confirmation shortly.
            </p>
          )}

          <div className="pt-4">
            <Button
              onClick={() => navigate('/settings')}
              className="w-full"
              data-testid="continue-btn"
            >
              Continue to Dashboard
              <ArrowRight className="ml-2" size={16} />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
